import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { RotateCcw, MapPin, Calendar, Percent, Trash2, Truck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CAPACITY_LABELS = {
  PLATEAU: "Plateau (1 véhicule)",
  DOUBLE_ETAGE: "Double étage (2 véhicules)",
  PORTE_VOITURES: "Porte-voitures ouvert (5-9)",
  PORTE_VOITURES_FERME: "Porte-voitures fermé (3-6)",
};

const DEFAULT_PLACES = { PLATEAU: 1, DOUBLE_ETAGE: 2, PORTE_VOITURES: 6, PORTE_VOITURES_FERME: 4 };

const STATUS_UI = {
  ACTIVE: ["Actif", "bg-emerald-100 text-emerald-800"],
  MATCHED: ["Matché ✓", "bg-orange-100 text-orange-800"],
  FULL: ["Complet ✓", "bg-orange-100 text-orange-800"],
  EXPIRED: ["Expiré", "bg-slate-100 text-slate-500"],
  WITHDRAWN: ["Retiré", "bg-slate-100 text-slate-500"],
};

export default function ProReturnTrips() {
  const [params] = useSearchParams();
  const [cities, setCities] = useState([]);
  const [trips, setTrips] = useState([]);
  const [sending, setSending] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    origin: params.get("origin") || "",
    destination: params.get("destination") || "",
    window_start: today,
    window_end: new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10),
    capacity_type: "PLATEAU",
    discount_pct: 30,
    places: "",
    source_request_id: params.get("from") || "",
  });

  const load = async () => {
    try {
      const [c, t] = await Promise.all([api.get("/cities"), api.get("/pro/return-trips")]);
      setCities(c.data);
      setTrips(t.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cityByName = (name) => cities.find(c => c.city === name);

  const publish = async (e) => {
    e.preventDefault();
    const o = cityByName(form.origin);
    const d = cityByName(form.destination);
    if (!o || !d) { toast.error("Choisissez les villes de départ et d'arrivée."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/pro/return-trips", {
        origin: { city: o.city, country: o.country, lat: o.lat, lng: o.lng },
        destination: { city: d.city, country: d.country, lat: d.lat, lng: d.lng },
        window_start: form.window_start,
        window_end: form.window_end,
        capacity_type: form.capacity_type,
        discount_pct: Number(form.discount_pct),
        places: form.places ? Number(form.places) : null,
        source_request_id: form.source_request_id || null,
      });
      toast.success(
        data.clients_notified > 0
          ? `Trajet publié — ${data.clients_notified} client(s) sur l'axe notifié(s)`
          : "Trajet retour publié"
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const withdraw = async (trip_id) => {
    try {
      await api.delete(`/pro/return-trips/${trip_id}`);
      toast.info("Trajet retiré");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const input = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="return-trips-page">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-4xl">
          <div className="flex items-center gap-2 mb-1">
            <RotateCcw className="w-5 h-5 text-orange-600" />
            <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Zéro trajet à vide</div>
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight mb-1">Mes trajets retour</h1>
          <p className="text-sm text-slate-500 mb-5">
            Publiez vos retours disponibles : les demandes compatibles vous sont présentées en priorité
            et les clients de l'axe sont alertés. Un retour matché est monétisé sur le même mouvement.
          </p>

          {/* Formulaire de publication */}
          <form onSubmit={publish} className="bg-white border border-slate-200 rounded-xl p-5 mb-6 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Départ (retour)</label>
              <select value={form.origin} onChange={e => setForm(f => ({ ...f, origin: e.target.value }))} className={input} data-testid="rt-origin">
                <option value="">— Ville —</option>
                {cities.map(c => <option key={`${c.city}-${c.country}`} value={c.city}>{c.city} ({c.country})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Arrivée (retour)</label>
              <select value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))} className={input} data-testid="rt-destination">
                <option value="">— Ville —</option>
                {cities.map(c => <option key={`${c.city}-${c.country}`} value={c.city}>{c.city} ({c.country})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Fenêtre — du</label>
              <input type="date" value={form.window_start} min={today}
                     onChange={e => setForm(f => ({ ...f, window_start: e.target.value }))} className={input} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">au</label>
              <input type="date" value={form.window_end} min={form.window_start}
                     onChange={e => setForm(f => ({ ...f, window_end: e.target.value }))} className={input} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Matériel</label>
              <select value={form.capacity_type} onChange={e => setForm(f => ({ ...f, capacity_type: e.target.value }))} className={input}>
                {Object.entries(CAPACITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Places disponibles</label>
              <input type="number" min="1" max="9" value={form.places}
                     placeholder={`${DEFAULT_PLACES[form.capacity_type] || 1} (défaut ${CAPACITY_LABELS[form.capacity_type]?.split(" (")[0] || "matériel"})`}
                     onChange={e => setForm(f => ({ ...f, places: e.target.value }))} className={input} data-testid="rt-places" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Décote proposée (retour à vide) : −{form.discount_pct} %</label>
              <input type="range" min="0" max="70" step="5" value={form.discount_pct}
                     onChange={e => setForm(f => ({ ...f, discount_pct: e.target.value }))} className="w-full accent-orange-600" />
            </div>
            {form.source_request_id && (
              <div className="sm:col-span-2 text-xs text-slate-500 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                Retour adossé à votre mission <strong>#{form.source_request_id}</strong> : la demande matchée
                deviendra la mission retour du même mouvement (double appariement).
              </div>
            )}
            <div className="sm:col-span-2">
              <Button type="submit" disabled={sending} className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto" data-testid="rt-publish">
                <Truck className="w-4 h-4 mr-2" /> {sending ? "Publication…" : "Publier ce trajet retour"}
              </Button>
            </div>
          </form>

          {/* Liste */}
          {trips.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400 text-sm">
              Aucun trajet retour publié pour l'instant.
            </div>
          ) : (
            <div className="space-y-2">
              {trips.map(t => {
                const [label, chip] = STATUS_UI[t.status] || [t.status, "bg-slate-100 text-slate-600"];
                return (
                  <div key={t.trip_id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${chip}`}>{label}</span>
                    <span className="font-display font-bold text-slate-900 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-orange-600" />{t.origin.city}
                      <span className="text-slate-300">→</span>{t.destination.city}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> {t.window_start} → {t.window_end}
                    </span>
                    <span className="text-xs text-slate-500">{CAPACITY_LABELS[t.capacity_type] || t.capacity_type}</span>
                    {t.places > 0 && ["ACTIVE", "FULL"].includes(t.status) && (
                      <span className={`text-xs font-semibold ${t.places_left > 0 ? "text-slate-700" : "text-orange-700"}`}>
                        {t.places_left}/{t.places} place{t.places > 1 ? "s" : ""}
                      </span>
                    )}
                    {t.discount_pct > 0 && (
                      <span className="text-xs font-semibold text-emerald-700 flex items-center gap-0.5">
                        <Percent className="w-3 h-3" />−{t.discount_pct} %
                      </span>
                    )}
                    {(t.matched_request_ids || []).map((rid, i) => (
                      <a key={rid} href={`/requests/${rid}`} className="text-xs text-orange-600 hover:underline flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Mission {i + 1}
                      </a>
                    ))}
                    {t.status === "MATCHED" && t.matched_request_id && (
                      <a href={`/requests/${t.matched_request_id}`} className="text-xs text-orange-600 hover:underline flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Mission retour
                      </a>
                    )}
                    {t.status === "ACTIVE" && (
                      <button onClick={() => withdraw(t.trip_id)}
                              className="ml-auto p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                              title="Retirer ce trajet">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
